import { useContext, FunctionComponent, useState } from "react";
import { Breadcrumbs } from "../../view/Navigation/BreadCrumbs";
import { useCompanyLicensesQuery } from "../../graphql/Queries/Licenses/GetCompanyLicenses";
import { Placeholder, PlaceholderWrapper } from "../../components/placeholder";
import { Image, Label, Button } from "grabcad-ui-elements";
import PlaceholderSvg from "../../images/placeholder_licenses.svg";
import { IScreenProps } from "../Screen";
import { ProductData, DetailedLicenseData, SdkIcon, useCompanyAsAdminQuery } from "../../graphql";
import { InfoBox } from "../../components/Styles";
import { ApplicationContext } from "../../components/ApplicationProvider";
import { ICON_MAP } from "../SDK/SdksOverview";

import ShopLogoSvg from "../../images/Shop_logo.svg";
import PrintLogoSvg from "../../images/print_logo.svg";
import SdkLogoSvg from "../../images/printer_connectivity_sdk.svg";
import MachineApiSvg from "../../images/machine_api_runtime.svg";
import ProtectAMLogoSvg from "../../images/protect_am_icon.svg";
import OpenAMLogoSvg from "../../images/OpenAM_logo.svg";
import defaultLicenseSvg from "../../images/thumb_licenses.svg";
import StreamlineProSvg from "../../images/streamline_pro_logomark.svg";
import AnalyzeSvg from "../../images/analyze_logomark.svg";
import { LicenseHeader, ProductLicensesOverview } from "./LicensesOverviewStyles";
import { LicenseFileData } from "@grabcad/user-license";
import { apiGet } from "../../zeus-client/Apis-wrapper";
import OfflineLicensingModal from "./DownloadLicenseFile/OfflineLicensingModal";
import { config } from "../../config";
import { Notifier } from "../../view/Notifier";
import { InfoBanner } from "./InfoBanner";
import { isValidMachineCode } from "@grabcad/company-server-shared/dist/types";

// TODO: Centralize these strings
const SHOP_APPLICATION_TYPE = "shop";
const SDK_APPLICATION_TYPE = "sdk";
const API_APPLICATION_TYPE = "api";
const PRINT_APPLICATION_TYPE = "print";
const PRINT_MANAGER_APPLICATION_TYPE = "print_manager";
const OPEN_MATERIALS_APPLICATION_TYPE = "open_materials";
const PROTECT_AM = "project_am";
const STREAMLINE_PRO = "streamline";
const ANALYZE = "analyze";
const GENERIC_LICENSE_ICON = { path: defaultLicenseSvg, height: 80 };

/**
 * @todo Allow image upload on the product creation screen and use those images instead of making this based on application type
 */
const icons: { [key: string]: { path: string; height: number } } = {
    [SHOP_APPLICATION_TYPE]: { path: ShopLogoSvg, height: 128 }, // This SVG has built in padding on top and bottom
    [SDK_APPLICATION_TYPE]: { path: SdkLogoSvg, height: 80 },
    [API_APPLICATION_TYPE]: { path: MachineApiSvg, height: 80 },
    [PRINT_APPLICATION_TYPE]: { path: PrintLogoSvg, height: 80 },
    [PRINT_MANAGER_APPLICATION_TYPE]: GENERIC_LICENSE_ICON, // May be replaced by Control logo soon?
    [OPEN_MATERIALS_APPLICATION_TYPE]: { path: OpenAMLogoSvg, height: 80 },
    [PROTECT_AM]: { path: ProtectAMLogoSvg, height: 80 },
    [STREAMLINE_PRO]: { path: StreamlineProSvg, height: 63 }, // This height makes the rounded rect match Shop et al.'s logomark dimensions
    [ANALYZE]: { path: AnalyzeSvg, height: 63 }, // This height makes the rounded rect match Shop et al.'s logomark dimensions
};

type SdkIcons = {
    [icon in SdkIcon]: {
        path: string;
        height: number;
    };
};

const sdkIcons = (Object.keys(ICON_MAP) as SdkIcon[]).reduce((res: SdkIcons, icon: SdkIcon) => {
    res[icon] = { path: ICON_MAP[icon], height: 80 };
    return res;
}, {} as SdkIcons);

export const LicensesOverview: FunctionComponent<IScreenProps> = (props) => {
    const { t } = useContext(ApplicationContext);
    const { companyLicenses, loadingLicenses, allEnabledFeatureKeys } = useCompanyLicensesQuery();
    const [showDownloadLicenseFileModal, setDownloadLicenseFileModal] = useState(false);
    const { company } = useCompanyAsAdminQuery();
    const hasGrabCADPrintProLicense =
        allEnabledFeatureKeys.has("gcp_pro_release") || allEnabledFeatureKeys.has("gcp_pro_fdm");
    const disableAction = !hasGrabCADPrintProLicense;
    const [machineCode, setMachineCode] = useState("");
    const [machineCodeError, setMachineCodeError] = useState<string | undefined>();

    const MACHINE_CODE_ERROR = "Machine code must be in format XXX-XXX-XXX";

    const handleMachineCodeChange = (value: string) => {
        const formatted = value.toUpperCase();
        setMachineCode(formatted);

        if (!formatted) {
            setMachineCodeError(undefined);
            return;
        }
        if (formatted.length < 11) {
            setMachineCodeError(MACHINE_CODE_ERROR);
        }
        if (!isValidMachineCode(formatted)) {
            setMachineCodeError(MACHINE_CODE_ERROR);
        } else {
            setMachineCodeError(undefined);
        }
    };

    const getProductIcon = (product: ProductData) => {
        if (product.sdk) {
            return sdkIcons[product.sdk.icon];
        } else if (product.applicationType) {
            if (product.name.toLowerCase().includes("protectam")) {
                // "ProtectAM Downloads" in prod
                // ProtectAM has generic 'print_manager' app type, but we have a special logo for it.
                // Unfortunately with current business logic we're forced to use its display name
                // as a magic string.
                return icons[PROTECT_AM];
            } else if (product.name.toLowerCase().includes("streamline")) {
                // "GrabCAD Streamline Pro" in prod
                // Similar to above, Streamline Pro has an 'any' applicationType.id.
                return icons[STREAMLINE_PRO];
            }
            return icons[product.applicationType.id] || GENERIC_LICENSE_ICON;
        } else {
            return GENERIC_LICENSE_ICON;
        }
    };

    const renderProductLogo = (product: ProductData) => {
        const icon = getProductIcon(product);
        return (
            icon && (
                <div className="imgHolder">
                    <Image src={icon.path} height={icon.height} />
                </div>
            )
        );
    };

    const renderActiveProduct = (product: ProductData, licenses: DetailedLicenseData[]) => {
        let totalSeats = 0;
        let totalSeatsUsed = 0;
        let earliestEndDate = licenses[0].endDate;
        const groupIds = new Set<number>();
        const serials: string[] = [];
        licenses.forEach((license) => {
            if (license.endDate && !earliestEndDate) {
                earliestEndDate = license.endDate;
            } else if (earliestEndDate && license.endDate && earliestEndDate > license.endDate) {
                earliestEndDate = new Date(license.endDate);
            }
            totalSeats += license.maxUsers;
            totalSeatsUsed += license.seatsUsed;
            license.groups.forEach((group) => {
                groupIds.add(group.id);
            });
            license.machineEntitlements?.forEach((scope) => {
                if (scope.serial) {
                    serials.push(scope.serial);
                }
            });
        });
        let anyUnlimitedLicense = licenses.some((license) => !!license.package.unlimitedMaxUsers);
        let hasCompanyWideLicense = licenses.some((license) => !!license.package.companyWide);
        let availableSeats: string = anyUnlimitedLicense
            ? t("license.unlimited")
            : (totalSeats - totalSeatsUsed).toString();
        if (hasCompanyWideLicense) {
            availableSeats += `, ${t("license.company_wide")}`;
        }

        return (
            <ProductLicensesOverview
                id="qa-licenseOverview-viewProductLicenses"
                onClick={() => props.history?.push(`/product/${product.id}`)}
                key={product.id}
                disabled={licenses.length === 0}
            >
                {renderProductLogo(product)}
                <div>
                    {}
                    <h2>{product.name}</h2>
                    <h2>{hasGrabCADPrintProLicense}</h2>
                    <dl>
                        {!anyUnlimitedLicense && (
                            <>
                                <dt>{t("licenses_overview.total_seats")}</dt>
                                <dd>{totalSeats}</dd>
                            </>
                        )}
                        {product.applicationType &&
                            product.applicationType.id !== API_APPLICATION_TYPE && (
                                <>
                                    <dt>{t("licenses_overview.available_seats")}</dt>
                                    <dd>{availableSeats}</dd>
                                    <dt>{t("licenses_overview.used_seats")}</dt>
                                    <dd>{totalSeatsUsed}</dd>
                                    <dt>{t("licenses_overview.num_user_groups")}</dt>
                                    <dd>{groupIds.size}</dd>
                                    <dt>{t("licenses_overview.serial_numbers")}</dt>
                                    <dd>{serials.join(", ")}</dd>
                                </>
                            )}
                        <dt>{t("licenses_overview.exp_date")}</dt>
                        <dd>
                            {earliestEndDate
                                ? new Date(earliestEndDate).toLocaleDateString("en-US")
                                : "-"}
                        </dd>
                    </dl>
                </div>
            </ProductLicensesOverview>
        );
    };

    const renderExpiredProduct = (product: ProductData, licenses: DetailedLicenseData[]) => {
        let earliestEndDate = licenses[0].endDate;
        licenses.forEach((license) => {
            if (license.endDate && !earliestEndDate) {
                earliestEndDate = license.endDate;
            } else if (earliestEndDate && license.endDate && earliestEndDate > license.endDate) {
                earliestEndDate = new Date(license.endDate);
            }
        });

        return (
            <ProductLicensesOverview
                id="qa-licenseOverview-viewProductLicenses"
                key={product.id}
                disabled={true}
                style={{ cursor: "not-allowed" }}
            >
                <Label ribbon="right" color="red">
                    {t("license.states.expired")}
                </Label>
                {renderProductLogo(product)}
                <div>
                    <h2>{product.name}</h2>
                    <dl>
                        <dt>{t("licenses_overview.expired_date")}</dt>
                        <dd style={{ color: "red" }}>
                            {earliestEndDate
                                ? new Date(earliestEndDate).toLocaleDateString("en-US")
                                : "-"}
                        </dd>
                    </dl>
                    {/* This will need to be dynamic once multiple products are supported */}
                    <p style={{ marginTop: 15 }}>{t("licenses_overview.license_expired_msg")}</p>
                </div>
            </ProductLicensesOverview>
        );
    };

    const renderNotStartedProduct = (product: ProductData, licenses: DetailedLicenseData[]) => {
        let earliestStartDate = licenses[0].startDate;
        licenses.forEach((license) => {
            if (earliestStartDate > license.startDate) {
                earliestStartDate = new Date(license.startDate);
            }
        });

        return (
            <ProductLicensesOverview
                id="qa-licenseOverview-viewProductLicenses"
                key={product.id}
                disabled={true}
                style={{ cursor: "not-allowed" }}
            >
                {renderProductLogo(product)}
                <div>
                    <h2>{product.name}</h2>
                    <dl>
                        <dt>{t("licenses_overview.earliest_start_date")}</dt>
                        <dd>{new Date(earliestStartDate).toLocaleDateString("en-US")}</dd>
                    </dl>
                    {/* This will need to be dynamic once multiple products are supported */}
                    <p style={{ marginTop: 15 }}>
                        {t("licenses_overview.license_not_started_msg")}
                    </p>
                </div>
            </ProductLicensesOverview>
        );
    };

    const title = (
        <h2 className="page-header" data-testid="licenses-overview-page-header">
            {t("licenses_overview.header")}
        </h2>
    );

    let placeholder: null | JSX.Element = null;
    if (companyLicenses?.licenses.length === 0) {
        placeholder = (
            <PlaceholderWrapper>
                {title}
                <Placeholder id="qa-companyLicenses-placeholder" data-testid="no-licenses">
                    <Image src={PlaceholderSvg} />
                    <h2>{t("licenses_overview.no_licenses")}</h2>
                </Placeholder>
            </PlaceholderWrapper>
        );
    }

    // organize our licenses into groups based on product
    const productLicenses: { [key: string]: DetailedLicenseData[] } = {};
    companyLicenses?.licenses.forEach((licenseData) => {
        if (!licenseData.package?.product) {
            return;
        }
        const productId = licenseData.package.product.id;
        productLicenses[productId] = productLicenses[productId] || [];
        productLicenses[productId].push(licenseData);
    });
    // in case we have two products with the same name, use the id
    const overviewCards = Object.keys(productLicenses).map((productId) => {
        const productLicensesData = productLicenses[productId];
        if (!productLicensesData?.length) {
            return null;
        }
        const product = productLicensesData[0].package.product as ProductData;
        const activeLicenses = productLicensesData.filter(
            (license) => license.state === "active" || license.state === "expiring"
        );
        const expiredLicenses = productLicensesData.filter(
            (license) => license.state === "expired"
        );
        const notStartedLicenses = productLicensesData.filter(
            (license) => license.state === "not_started"
        );
        if (activeLicenses.length > 0) {
            return renderActiveProduct(product, activeLicenses);
        }
        if (notStartedLicenses.length > 0) {
            return renderNotStartedProduct(product, notStartedLicenses);
        }
        return renderExpiredProduct(product, expiredLicenses);
    });

    const openDownloadLicenseModal = () => {
        setMachineCode("");
        setMachineCodeError(undefined);
        setDownloadLicenseFileModal(true);
    };

    const downloadLicenseFile = async (): Promise<void> => {
        if (!isValidMachineCode(machineCode)) {
            setMachineCodeError(MACHINE_CODE_ERROR);
            return;
        }
        try {
            const LICENSES_ROUTE = `${
                config.REACT_APP_BASE_URL
            }/v1/licenses?machineCode=${encodeURIComponent(machineCode)}&mode=OFFLINE`;

            const response: LicenseFileData = await apiGet<LicenseFileData>(LICENSES_ROUTE);

            const blob = new Blob([JSON.stringify(response)], {
                type: "application/json",
            });

            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            Object.assign(a, { href: url, download: "license_file.json" });

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            setDownloadLicenseFileModal(false);
            setMachineCode("");
            setMachineCodeError(undefined);

            Notifier.success(t("licenses_overview.download_license_file.download_success"));
        } catch (error) {
            Notifier.fail(t("licenses_overview.download_license_file.download_failed"));
        }
    };

    return (
        <>
            <Breadcrumbs sections={[{ label: t("licenses_overview.breadcrumb") }]} />
            {loadingLicenses && <div className="ui loader active" />}

            {placeholder || (
                <div style={{ paddingBottom: 25 }}>
                    <LicenseHeader data-testid="heading-license">
                        {title}
                        <Button
                            className="download-license-btn"
                            data-testid="download-license-btn"
                            onClick={openDownloadLicenseModal}
                            disabled={disableAction}
                        >
                            {t("licenses_overview.download_license_file.button_text")}
                        </Button>
                    </LicenseHeader>
                    {disableAction &&
                        companyLicenses?.licenses.length !== 0 &&
                        company?.internal && <InfoBanner />}
                    <InfoBox>{t("licenses_overview.info")}</InfoBox>
                    {overviewCards}
                </div>
            )}
            {showDownloadLicenseFileModal && (
                <OfflineLicensingModal
                    isOpen={showDownloadLicenseFileModal}
                    machineCode={machineCode}
                    error={machineCodeError}
                    onMachineCodeChange={handleMachineCodeChange}
                    onCancel={() => setDownloadLicenseFileModal(false)}
                    onConfirm={() => {
                        void downloadLicenseFile();
                    }}
                />
            )}
        </>
    );
};
